// signature-state.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

export interface SignatureState {
  signatoryIndex: number;
  hasSignatures: boolean;
  signatureCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class SignatureStateService {
  private signatureStatesSubject = new BehaviorSubject<
    Map<number, SignatureState>
  >(new Map());
  public signatureStates$ = this.signatureStatesSubject.asObservable();
  private currentRoute: string = '';

  constructor(private router: Router) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (this.currentRoute && this.currentRoute !== event.url) {
          this.clearAllStates();
        }
        this.currentRoute = event.url;
      });
  }

  updateSignatureState(
    signatoryIndex: number,
    hasSignatures: boolean,
    signatureCount: number = 0
  ) {
    const currentStates = this.signatureStatesSubject.value;
    currentStates.set(signatoryIndex, {
      signatoryIndex,
      hasSignatures,
      signatureCount
    });
    this.signatureStatesSubject.next(new Map(currentStates));
  }

  getSignatureState(index: number): SignatureState | undefined {
    return this.signatureStatesSubject.value.get(index);
  }

  hasSignatures(signatoryIndex: number): boolean {
    const state = this.getSignatureState(signatoryIndex);
    return state?.hasSignatures || false;
  }

  getSignatureCount(signatoryIndex: number): number {
    const state = this.getSignatureState(signatoryIndex);
    return state?.signatureCount || 0;
  }

  clearAllStates() {
    this.signatureStatesSubject.next(new Map());
  }

  removeSignatoryState(signatoryIndex: number) {
    const currentStates = this.signatureStatesSubject.value;
    currentStates.delete(signatoryIndex);
    this.signatureStatesSubject.next(new Map(currentStates));
  }

  // NEW: Clear signature state for a specific signatory
  clearSignatureState(index: number): void {
    const currentStates = this.signatureStatesSubject.value;
    currentStates.delete(index);
    this.signatureStatesSubject.next(new Map(currentStates));
  }

  // NEW: Re-index states after signatory removal
  reindexStates(removedIndex: number, totalSignatories: number): void {
    const currentStates = this.signatureStatesSubject.value;
    const newStates = new Map<number, SignatureState>();

    currentStates.forEach((state, index) => {
      if (index < removedIndex) {
        // Keep indices before removed index as is
        newStates.set(index, state);
      } else if (index > removedIndex) {
        // Shift indices after removed index down by 1
        const newState = {
          ...state,
          signatoryIndex: index - 1
        };
        newStates.set(index - 1, newState);
      }
      // Skip the removed index
    });

    this.signatureStatesSubject.next(newStates);
  }

  resetStatesOnPageChange() {
    this.clearAllStates();
  }

  clearStatesForRoutes(routePatterns: string[]) {
    const currentUrl = this.router.url;
    const shouldClear = routePatterns.some(
      (pattern) => currentUrl.includes(pattern) || !currentUrl.includes(pattern)
    );
    if (shouldClear) {
      this.clearAllStates();
    }
  }
}
